/* eslint-disable react-hooks/exhaustive-deps */
// @vendors
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

// @context
import { useOnboarding } from '../../context/OnboardingContext';
import { useAuth } from '../../context/AuthContext';

// @components
import OnboardingLayout, { CancelButton } from './OnboardingLayout';

// @services
import { OnboardingService, Document, CompanyResponse } from '../../services/onboarding.service';
import FirebaseStorageService from '../../services/firebase-storage.service';

// @utils
import { sendOnboardingEmail } from '../../utils/emailSender';
import FirebaseAuthService from '../../services/firebase-auth.service';

const TermsConditions: React.FC<{}> = () => {
  const navigate = useNavigate();
  const { companyData, updateCompanyData, setCurrentStep } = useOnboarding();
  const { currentUser } = useAuth();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Usar los datos de Google si están disponibles
  useEffect(() => {
    if (currentUser) {
      const fullName = currentUser.displayName || '';
      const nameParts = fullName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      if (firstName !== companyData.name ||
        lastName !== companyData.lastname ||
        currentUser.email !== companyData.email ||
        currentUser.photoURL !== companyData.profileImage) {
        updateCompanyData({
          name: firstName,
          lastname: lastName,
          email: currentUser.email || '',
          profileImage: currentUser.photoURL || null
        });
      }
    }
  }, [currentUser, companyData.name, companyData.lastname, companyData.email, companyData.profileImage]);

  // Manejar el cambio en el checkbox
  const handleCheckboxChange = () => {
    setTermsAccepted(!termsAccepted);
  };

  // Función para enviar email de bienvenida
  const enviarEmail = async () => {
    // Extraer datos del onboarding
    const userEmail = companyData.companyEmail || companyData.email || currentUser?.email || '';
    const userName = `${companyData.name || ''} ${companyData.lastname || ''}`.trim() || companyData.legalRepName || 'Usuario';
    const companyName = companyData.companyName || companyData.name || 'Empresa';
    const currentDate = new Date().toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const emailData = {
      toEmail: userEmail,
      toName: userName,
      templateId: "3zxk54vynnq4jy6v",
      subject: `¡Bienvenido a Xervix, ${companyName}!`,
      templateData: {
        company: companyName,
        date: currentDate
      },
      fromEmail: "admon@xervix.com.co",
      fromName: "XERVIX",
      onboardingKey: "XERVIX_ONBOARDING_2024" // 🔑 Clave de seguridad
    };

    try {
      await sendOnboardingEmail(emailData);
    } catch (error) {
    }
  };

  const handleBack = () => {
    setCurrentStep(7);
    navigate('/onboarding/lead-price');
  };

  // Manejar el envío del formulario
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Verificar que el usuario haya aceptado los términos
      if (!termsAccepted) {
        setError('Debes aceptar los términos y condiciones para continuar.');
        setIsSubmitting(false);
        return;
      }

      // Verificar si el usuario está autenticado
      if (!currentUser) {
        setError('Debes iniciar sesión antes de continuar.');
        setIsSubmitting(false);
        return;
      }

      // Verificar si tenemos un token JWT válido
      const authToken = localStorage.getItem('auth_token');
      if (!authToken) {
        setError('No se encontró un token de autenticación válido. Por favor, inicia sesión nuevamente.');
        setIsSubmitting(false);
        return;
      }

      // Actualizar los datos en el contexto
      updateCompanyData({
        termsAccepted: true
      });

      // Procesar documentos para usar las URLs de Firebase Storage
      let formattedDocuments: Document[] = [];
      if (companyData.documents && companyData.documents.length > 0) {
        formattedDocuments = companyData.documents
          .filter(doc => doc.url && doc.url.length > 0)
          .map(doc => {
            return {
              category: doc.category || '',
              name: doc.name || '',
              size: doc.size || 0,
              type: doc.type || '',
              url: doc.url || '',
              content: '' // Campo vacío para mantener compatibilidad con la interfaz Document
            };
          });
      }

      // Usar las imágenes de servicio directamente (solo URLs)
      let processedServiceImages: string[] = [];
      if (companyData.serviceImages && companyData.serviceImages.length > 0) {
        // Filtrar solo las imágenes que son URLs válidas
        processedServiceImages = companyData.serviceImages
          .filter(image => {
            const isValidUrl = typeof image === 'string' && (image.startsWith('http://') || image.startsWith('https://'));
            return isValidUrl;
          });
      }
      // Obtener la URL de la imagen de perfil desde localStorage
      const savedProfileImage = localStorage.getItem('userProfileImage');
      let finalProfileUrl = '';

      if (savedProfileImage) {
        finalProfileUrl = savedProfileImage;
      } else if (typeof companyData.profileImage === 'string' && companyData.profileImage) {
        finalProfileUrl = companyData.profileImage;
      } else if (currentUser?.photoURL) {
        finalProfileUrl = currentUser.photoURL;
      }

      // Crear el objeto final con la estructura exacta que espera el backend
      const finalCompanyData = {
        email: currentUser?.email || companyData.email || '',
        name: currentUser?.displayName?.split(' ')[0] || companyData.name || '',
        lastName: currentUser?.displayName?.split(' ').slice(1).join(' ') || companyData.lastname || '',
        profilePictureUrl: finalProfileUrl,
        isActive: true,
        googleId: currentUser?.uid || '',
        // Datos de la empresa
        category: companyData.category || '',
        companyDescription: companyData.companyDescription || '',
        documents: formattedDocuments,
        employeeCount: Number(companyData.employeeCount) || 0,
        leads: 0,
        legalRepId: companyData.legalRepId || '',
        legalRepName: companyData.legalRepName || '',
        locations: companyData.locations || [],
        phone: companyData.phone || '',
        // Usar la URL final determinada anteriormente
        profileImage: finalProfileUrl,
        serviceImages: processedServiceImages, // Imágenes de servicio procesadas
        services: companyData.services || [],
        state: 'pending',
        termsAccepted: true,
        yearsExperience: Number(companyData.yearsExperience) || 0,
        leadPrice: Number(companyData.leadPrice) || 0,
        Role: 'company',
        PasswordHash: "$2a$11$placeholder_hash_for_companies"
      };

      const response = await OnboardingService.submitCompanyData(finalCompanyData);
      saveCompanyData(response);
      // Si hay archivos para subir (solo imagen de perfil)
      if (response && response.id) {
        const companyId = response.id;

        // Subir imagen de perfil si existe y es un archivo (no una URL)
        if (companyData.profileImage && typeof companyData.profileImage !== 'string') {
          try {
            const profileImageUrl = await FirebaseStorageService.uploadFile(
              companyData.profileImage,
              `companies/${companyId}/profile`
            );

            // Actualizar la URL de la imagen de perfil en el backend
            if (profileImageUrl) {
              await OnboardingService.updateProfileImage(profileImageUrl, companyId);
            }
          } catch (err) {
            toast.error('Error al subir imagen de perfil a Firebase Storage');
          }
        }
      }
      enviarEmail();
      setCurrentStep(8); // Avanzar al siguiente paso
      navigate('/onboarding/congratulations'); // Navegar a la página de felicitaciones
    } catch (err) {
      setError('Hubo un error al enviar los datos. Por favor, intenta nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveCompanyData = async (response: CompanyResponse) => {
    await FirebaseAuthService.getTokenForUser();
    localStorage.setItem('company_id', response.id);
    localStorage.setItem('user_state', 'pending');
  }

  return (
    <OnboardingLayout
      title="¡Transparencia ante todo!"
      subtitle="Antes de comenzar, te invitamos a leer y aceptar nuestros Términos y Condiciones. Queremos que esta alianza sea segura, clara y justa para todos."
      showBackButton={true}
      onBack={handleBack}
    >
      <form onSubmit={handleSubmit} className="space-y-2">
        <div className="space-y-4">
          <div className='flex justify-between items-center'>
            <h2 className="text-xl font-semibold text-xervix-text-primary dark:text-xervix-text-primary-dark">
              Términos y condiciones
            </h2>
            <p className="text-sm text-xervix-text-secondary dark:text-xervix-text-secondary-dark">
              Última actualización 5/12/2024
            </p>

          </div>

          {/* Contenedor con overflow para los términos y condiciones */}
          <div className="bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark rounded-lg p-4 md:h-[200px] h-[380px] overflow-y-auto">
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2 text-xervix-text-primary dark:text-xervix-text-primary-dark">Clause 1</h3>
                <p className="text-sm text-xervix-text-primary dark:text-xervix-text-primary-dark">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Viverra condimentum eget purus in. Consectetur eget id morbi amet amet, in. Ipsum viverra pretium tellus neque. Ullamcorper suspendisse aenean leo pharetra in sit semper et. Amet quam placerat sem.
                </p>
                <p className="text-sm text-xervix-text-primary dark:text-xervix-text-primary-dark mt-2">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Viverra condimentum eget purus in. Consectetur eget id morbi amet amet, in. Ipsum viverra pretium tellus neque. Ullamcorper suspendisse aenean leo pharetra in sit semper et. Amet quam placerat sem.
                </p>
              </div>

              <div>
                <h3 className="font-medium mb-2 text-xervix-text-primary dark:text-xervix-text-primary-dark">Clause 2</h3>
                <p className="text-sm text-xervix-text-primary dark:text-xervix-text-primary-dark">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Viverra condimentum eget purus in. Consectetur eget id morbi amet amet, in. Ipsum viverra pretium
                </p>
              </div>

              <div>
                <h3 className="font-medium mb-2 text-xervix-text-primary dark:text-xervix-text-primary-dark">Clause 3</h3>
                <p className="text-sm text-xervix-text-primary dark:text-xervix-text-primary-dark">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Viverra condimentum eget purus in. Consectetur eget id morbi amet amet, in. Ipsum viverra pretium tellus neque. Ullamcorper suspendisse aenean leo pharetra in sit semper et. Amet quam placerat sem.
                </p>
              </div>

              <div>
                <h3 className="font-medium mb-2 text-xervix-text-primary dark:text-xervix-text-primary-dark">Clause 4</h3>
                <p className="text-sm text-xervix-text-primary dark:text-xervix-text-primary-dark">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Viverra condimentum eget purus in. Consectetur eget id morbi amet amet, in. Ipsum viverra pretium tellus neque. Ullamcorper suspendisse aenean leo pharetra in sit semper et. Amet quam placerat sem.
                </p>
              </div>

              <div>
                <h3 className="font-medium mb-2 text-xervix-text-primary dark:text-xervix-text-primary-dark">Clause 5</h3>
                <p className="text-sm text-xervix-text-primary dark:text-xervix-text-primary-dark">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Viverra condimentum eget purus in. Consectetur eget id morbi amet amet, in. Ipsum viverra pretium tellus neque. Ullamcorper suspendisse aenean leo pharetra in sit semper et. Amet quam placerat sem.
                </p>
              </div>
            </div>
          </div>

          {/* Checkbox para aceptar términos */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="terms-checkbox"
              checked={termsAccepted}
              onChange={handleCheckboxChange}
              className="w-4 h-4 text-xervix-primary bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark border-xervix-border dark:border-xervix-border-dark rounded focus:ring-xervix-primary"
            />
            <label htmlFor="terms-checkbox" className="ml-2 text-sm text-xervix-text-primary dark:text-xervix-text-primary-dark">
              Acepto las condiciones
            </label>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        <div className="flex justify-center gap-4 mt-8">
          <CancelButton />
          <button
            type="submit"
            disabled={!termsAccepted || isSubmitting}
            className={`px-8 py-3 rounded-md text-white font-medium transition-colors ${!termsAccepted || isSubmitting
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-xervix-primary text-white hover:bg-opacity-90'
              }`}
          >
            {isSubmitting ? 'Enviando...' : 'Continuar'}
          </button>
        </div>
      </form>
    </OnboardingLayout>
  );
};

export default TermsConditions;
